import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  tableCellClasses,
  styled,
} from '@mui/material';
import { Integration, IntegrationNetwork } from '../../types';
import * as Styled from './NetworksTable.styles';

// Cтилизация ячеек
const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 13,
    fontWeight: 700,
    padding: '6px 8px',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '4px 8px',
  },
}));

interface NetworksTableProps {
  integration: Integration | null;
  mutationLoading: boolean;
  onAddNetworkClick: () => void;
  onRemoveNetworkClick: (networkCode: string) => void;
  onToggleActiveClick: (networkCode: string, active: boolean) => void;
  onEditInfoClick: () => void;
}

// Отрисовка одной таблицы сетей (активных или неактивных)
const NetworksSubTable: React.FC<{
  title: string;
  rows: IntegrationNetwork[];
  isActive: boolean;
  disabled: boolean;
  onRemove: (code: string) => void;
  onToggle: (code: string, active: boolean) => void;
}> = ({ title, rows, isActive, disabled, onRemove, onToggle }) => {
  const { t } = useTranslation();
  if (!rows || rows.length === 0) return null;

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <StyledTableCell colSpan={6}>{title}</StyledTableCell>
          </TableRow>
          <TableRow>
            <StyledTableCell>
              {t('IntegrationsPage.Networks.CODE')}
            </StyledTableCell>
            <StyledTableCell>
              {t('IntegrationsPage.Networks.NAME')}
            </StyledTableCell>
            <StyledTableCell>
              {t('IntegrationsPage.Networks.LEGAL_NAME')}
            </StyledTableCell>
            <StyledTableCell>
              {t('IntegrationsPage.Networks.PHONE')}
            </StyledTableCell>
            <StyledTableCell>
              {t('IntegrationsPage.Networks.ROUNDING')}
            </StyledTableCell>
            <StyledTableCell align="right">
              {t('IntegrationsPage.Networks.ACTIONS')}
            </StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((n) => (
            <TableRow key={n.code} hover>
              <StyledTableCell>{n.code}</StyledTableCell>
              <StyledTableCell>{n.name}</StyledTableCell>
              <StyledTableCell>{n.legal_name}</StyledTableCell>
              <StyledTableCell>{n.phone}</StyledTableCell>
              <StyledTableCell>
                {n.rounding_method || '-'} / {n.rounding_rule ?? '-'}
              </StyledTableCell>
              <StyledTableCell align="right">
                <Styled.ButtonContainer>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={disabled}
                    onClick={() => onToggle(n.code, !isActive)}
                  >
                    {isActive
                      ? t('IntegrationsPage.Networks.DEACTIVATE')
                      : t('IntegrationsPage.Networks.ACTIVATE')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={disabled}
                    onClick={() => onRemove(n.code)}
                  >
                    {t('IntegrationsPage.Networks.REMOVE')}
                  </Button>
                </Styled.ButtonContainer>
              </StyledTableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Таблица сетей выбранной интеграции (активные + неактивные)
export const NetworksTable: React.FC<NetworksTableProps> = ({
  integration,
  mutationLoading,
  onAddNetworkClick,
  onRemoveNetworkClick,
  onToggleActiveClick,
  onEditInfoClick,
}) => {
  const { t } = useTranslation();

  if (!integration) {
    return <div>{t('IntegrationsPage.Networks.EMPTY') as string}</div>;
  }

  return (
    <Styled.Wrapper>
      <Styled.ButtonContainer style={{ flexDirection: 'row', gap: 8 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={onEditInfoClick}
          disabled={mutationLoading}
        >
          {t('IntegrationsPage.Actions.EDIT_INFO')}
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={onAddNetworkClick}
          disabled={mutationLoading}
        >
          {t('IntegrationsPage.Actions.ADD_NETWORK')}
        </Button>
      </Styled.ButtonContainer>

      <NetworksSubTable
        title={t('IntegrationsPage.Networks.ACTIVE_TITLE')}
        rows={integration.networks || []}
        isActive
        disabled={mutationLoading}
        onRemove={onRemoveNetworkClick}
        onToggle={onToggleActiveClick}
      />

      <NetworksSubTable
        title={t('IntegrationsPage.Networks.INACTIVE_TITLE')}
        rows={integration.inactive_networks || []}
        isActive={false}
        disabled={mutationLoading}
        onRemove={onRemoveNetworkClick}
        onToggle={onToggleActiveClick}
      />
    </Styled.Wrapper>
  );
};

export default NetworksTable;
